import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router'

import { PrimeIcons } from 'primereact/api'

import { Permission, usePermissionsContext } from '..'
import { useCurrentTenantConfig } from '../../../utils/tenants'
import { Permissions } from '../constants'

/**
 * @component HomeGuard
 * @description A guard component that checks user permissions and navigates to the appropriate page.
 *
 * @returns {JSX.Element} A React component that either:
 * - Navigates to the appropriate page
 * - Shows a "No supplier selected" message
 * - Shows nothing
 */

type PermissionNavigationResult = {
  shouldNavigate: boolean
  navigateTo: string
}

const defaultResult: PermissionNavigationResult = {
  shouldNavigate: false,
  navigateTo: '',
}

export const HomeGuard: React.FC = () => {
  const { t } = useTranslation()
  const { projectId } = useCurrentTenantConfig()
  const { permissions } = usePermissionsContext()
  const [navigationResult, setNavigationResult] =
    useState<PermissionNavigationResult>(defaultResult)

  // Check if the user has permissions and set the navigation target
  useEffect(() => {
    if (permissions) {
      const result = getPermissionNavigationTarget(permissions.data)
      setNavigationResult(result)
    } else {
      setNavigationResult(defaultResult)
    }
  }, [projectId, permissions])

  // If the user has permissions, navigate to the appropriate page
  if (navigationResult.shouldNavigate) {
    return <Navigate to={navigationResult.navigateTo} />
  }

  // If no supplier is selected, show a message
  if (!projectId)
    return (
      <div className="flex flex-column align-items-center justify-content-center py-6">
        <div className="flex flex-column sm:flex-row">
          <div className="text-5xl text-primary font-bold border-left-1 sm:border-right-1 sm:border-left-none px-3 pb-3 sm:pb-0">
            <i className={`${PrimeIcons.INFO_CIRCLE} text-6xl`} />
          </div>
          <div className="flex flex-column border-left-1 sm:border-left-none border-primary px-3 gap-2">
            <div className="font-bold">{t('components.home_guard.text')}</div>
            <div>{t('components.home_guard.description')}</div>
          </div>
        </div>
      </div>
    )

  return null
}

/**
 * Determines the navigation target based on the user's permissions.
 *
 * @param permissions - Array of user permissions
 * @returns {PermissionNavigationResult} An object containing:
 *  - shouldNavigate: Whether navigation should occur
 *  - navigateTo: The navigation target (when shouldNavigate is true)
 */
function getPermissionNavigationTarget(
  permissions: Permission[] | undefined,
): PermissionNavigationResult {
  if (!permissions || permissions?.length === 0) return defaultResult

  let navigateTo = ''

  const webEdiPermissionNames = permissions
    .filter(p => p.name.startsWith('webedi'))
    .map(p => p.name)

  // Sort the permissions by priority:
  // The first one is less important and the last one is the most important
  // Priority 2: Tenant Permissions
  if (webEdiPermissionNames.includes(Permissions.Tenant.SupplierCockpit)) {
    navigateTo = '/suppliers'
  }

  // Priority 1: Project Permissions
  if (webEdiPermissionNames.includes(Permissions.Project.Trial)) {
    navigateTo = '/demo-functions'
  }

  if (webEdiPermissionNames.includes(Permissions.Project.ProjectUserAdmin)) {
    navigateTo = '/members'
  }

  if (webEdiPermissionNames.includes(Permissions.Project.EInvoice)) {
    navigateTo = '/e-invoices'
  }

  if (
    webEdiPermissionNames.includes(Permissions.Project.DeliveryForecastWorklist)
  ) {
    navigateTo = '/delivery-forecasts'
  }

  if (webEdiPermissionNames.includes(Permissions.Project.Worklist)) {
    navigateTo = '/orders'
  }

  if (navigateTo)
    return {
      shouldNavigate: true,
      navigateTo,
    }

  return defaultResult
}
